import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { api, ApiError, bindAuth } from "../lib/api";
import { adminDevicesApi } from "../lib/adminDevicesApi";
import { wipeAllOfflineChapters } from "../lib/offlineChapters";
import {
  type AuthTokens,
  isAccessExpired,
  isIdleExpired,
  mapTokenResponse,
} from "../lib/auth-tokens";

const STORAGE_KEY = "soorgaa_auth";
const LAST_ACTIVE_KEY = "soorgaa_admin_last_active";
const ACTIVITY_THROTTLE_MS = 5_000;
// A background refresh only counts as "active" if the admin did something this recently.
const ACTIVE_WINDOW_MS = 10 * 60 * 1000;

type AuthContextValue = {
  tokens: AuthTokens | null;
  isAuthenticated: boolean;
  role: AuthTokens["role"] | null;
  /** Admin idle lock: signed in, but asking for the password only. Page state is kept. */
  locked: boolean;
  setAuth: (tokens: AuthTokens) => void;
  logout: (force?: boolean) => void;
  unlock: (password: string) => Promise<void>;
  refreshSession: () => Promise<AuthTokens | null>;
  getAccessToken: () => Promise<string | null>;
};

const AuthContext = createContext<AuthContextValue | null>(null);

function readLastActive(): number {
  try {
    const raw = localStorage.getItem(LAST_ACTIVE_KEY);
    const n = raw ? Number(raw) : NaN;
    return Number.isFinite(n) ? n : Date.now();
  } catch {
    return Date.now();
  }
}

function writeLastActive(ts: number) {
  try {
    localStorage.setItem(LAST_ACTIVE_KEY, String(ts));
  } catch {
    // storage unavailable
  }
}

function idleLockDue(tokens: AuthTokens | null): boolean {
  if (!tokens || tokens.role !== "admin" || !tokens.idleLockMinutes) return false;
  return Date.now() - readLastActive() >= tokens.idleLockMinutes * 60_000;
}

function loadStored(): AuthTokens | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<AuthTokens>;
    if (!parsed.accessToken || !parsed.refreshToken || !parsed.role || !parsed.accessExpiresAt) {
      localStorage.removeItem(STORAGE_KEY);
      return null;
    }
    const tokens = parsed as AuthTokens;
    if (isIdleExpired(tokens)) {
      localStorage.removeItem(STORAGE_KEY);
      return null;
    }
    return tokens;
  } catch {
    return null;
  }
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [tokens, setTokens] = useState<AuthTokens | null>(loadStored);
  const [locked, setLocked] = useState<boolean>(() => idleLockDue(loadStored()));
  const tokensRef = useRef(tokens);
  tokensRef.current = tokens;
  const lockedRef = useRef(locked);
  lockedRef.current = locked;

  const logout = useCallback((force = false) => {
    // Automatic sign-outs (failed background refresh) must not drop a locked admin out of their page.
    if (!force && lockedRef.current) return;
    setLocked(false);
    lockedRef.current = false;
    setTokens(null);
    localStorage.removeItem(STORAGE_KEY);
    try {
      localStorage.removeItem(LAST_ACTIVE_KEY);
    } catch {
      // storage unavailable
    }
    wipeAllOfflineChapters();
    if ("serviceWorker" in navigator && navigator.serviceWorker.controller) {
      navigator.serviceWorker.controller.postMessage({ type: "CLEAR_USER_CACHES" });
    }
    if (typeof window !== "undefined" && window.location.pathname !== "/" && !window.location.pathname.startsWith("/auth")) {
      window.location.href = "/";
    }
  }, []);

  const setAuth = useCallback((next: AuthTokens) => {
    if (!tokensRef.current) writeLastActive(Date.now()); // fresh sign-in counts as activity
    setTokens(next);
    tokensRef.current = next;
    localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
  }, []);

  const refreshSession = useCallback(async (): Promise<AuthTokens | null> => {
    const run = async (retried: boolean): Promise<AuthTokens | null> => {
      const current = tokensRef.current;
      if (!current?.refreshToken) {
        logout();
        return null;
      }
      if (isIdleExpired(current)) {
        logout();
        return null;
      }
      if (lockedRef.current) return null;
      const active = Date.now() - readLastActive() < ACTIVE_WINDOW_MS;
      try {
        const res = await api.refreshSession(current.refreshToken, active);
        const next = mapTokenResponse(res);
        setAuth(next);
        return next;
      } catch (err) {
        if (err instanceof ApiError && err.status === 423) {
          setLocked(true);
          lockedRef.current = true;
          return null;
        }
        if (err instanceof ApiError && err.status === 409 && !retried) {
          // Another tab rotated the token a moment ago: pick up its tokens and try once more.
          const stored = loadStored();
          if (stored) {
            setTokens(stored);
            tokensRef.current = stored;
            return run(true);
          }
        }
        logout();
        return null;
      }
    };
    return run(false);
  }, [logout, setAuth]);

  const unlock = useCallback(
    async (password: string) => {
      const current = tokensRef.current;
      if (!current?.refreshToken) {
        logout(true);
        return;
      }
      // Throws ApiError (wrong password, too many attempts) to the lock screen.
      try {
        const res = await adminDevicesApi.unlock(current.refreshToken, password);
        const next = mapTokenResponse(res);
        writeLastActive(Date.now());
        setAuth(next);
        setLocked(false);
        lockedRef.current = false;
      } catch (err) {
        // Too many wrong attempts or session ended: full sign-in (OTP) is required.
        if (err instanceof ApiError && (err.status === 401 && /sign in again|expired|invalid/i.test(err.message))) {
          logout(true);
        }
        throw err;
      }
    },
    [logout, setAuth],
  );

  const getAccessToken = useCallback(async (): Promise<string | null> => {
    const current = tokensRef.current;
    if (!current) return null;
    if (lockedRef.current) return null;
    if (isIdleExpired(current)) {
      logout();
      return null;
    }
    if (!isAccessExpired(current)) return current.accessToken;
    const refreshed = await refreshSession();
    return refreshed?.accessToken ?? null;
  }, [logout, refreshSession]);

  useEffect(() => {
    bindAuth({
      getTokens: () => tokensRef.current,
      refreshSession,
      logout: () => logout(),
      getAccessToken,
      isLocked: () => lockedRef.current,
    });
    return () => bindAuth(null);
  }, [refreshSession, logout, getAccessToken]);

  // Silent background refresh + idle lock check.
  useEffect(() => {
    if (!tokens?.accessToken) return;
    const tick = () => {
      const current = tokensRef.current;
      if (!current || lockedRef.current) return;
      if (current.role === "admin" && isIdleExpired(current)) {
        logout();
        return;
      }
      if (idleLockDue(current)) {
        setLocked(true);
        lockedRef.current = true;
        return;
      }
      if (isAccessExpired(current, 120)) {
        void refreshSession();
      }
    };
    tick();
    const id = window.setInterval(tick, tokens.role === "admin" ? 30 * 1000 : 5 * 60 * 1000);
    let lastWrite = 0;
    const onActivity = () => {
      if (tokensRef.current?.role !== "admin" || lockedRef.current) return;
      const now = Date.now();
      if (now - lastWrite > ACTIVITY_THROTTLE_MS) {
        lastWrite = now;
        writeLastActive(now);
      }
      tick();
    };
    const onVisible = () => {
      if (document.visibilityState === "visible") tick();
    };
    const events = ["click", "keydown", "pointerdown", "touchstart", "scroll"] as const;
    events.forEach((e) => window.addEventListener(e, onActivity, { passive: true }));
    document.addEventListener("visibilitychange", onVisible);
    window.addEventListener("focus", tick);
    return () => {
      clearInterval(id);
      events.forEach((e) => window.removeEventListener(e, onActivity));
      document.removeEventListener("visibilitychange", onVisible);
      window.removeEventListener("focus", tick);
    };
  }, [tokens?.accessToken, tokens?.role, logout, refreshSession]);

  // Keep tabs in sync: tokens rotate on every refresh, and a lock/unlock in one tab applies to all.
  useEffect(() => {
    const onStorage = (e: StorageEvent) => {
      if (e.key === STORAGE_KEY) {
        const next = loadStored();
        setTokens(next);
        tokensRef.current = next;
        if (!next) {
          setLocked(false);
          lockedRef.current = false;
        } else if (lockedRef.current && !idleLockDue(next)) {
          setLocked(false);
          lockedRef.current = false;
        }
      }
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);

  const value = useMemo(
    () => ({
      tokens,
      isAuthenticated: Boolean(tokens?.accessToken),
      role: tokens?.role ?? null,
      locked,
      setAuth,
      logout,
      unlock,
      refreshSession,
      getAccessToken,
    }),
    [tokens, locked, setAuth, logout, unlock, refreshSession, getAccessToken],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
